// Renderless root for the data-centrifuge container, mounted by the shim. Owns no
// state itself — it subscribes to the plain Controller instance (created by the shim
// before React mounts) and re-renders the Chart Component + Responses on every
// notify(). The sidebar panel is repainted imperatively by controller.render(); this
// component only owns the two in-container React views, mirroring the legacy
// renderComponent / renderResponses.

import { useReducer, useEffect, useRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'

import { DEFAULT_FILTER, DEFAULT_SUSPICION_LEVEL } from '@aytm/app/stats/page/data-centrifuge/lib/vectors'
import { buildComponentProps } from '@aytm/app/stats/page/data-quality-report/controller/component-props-builder'

// components
import Component from '@aytm/app/stats/page/data-centrifuge/Component'
import Responses from '@aytm/app/stats/page/data-centrifuge/Responses'

// Map componentProps.data into the Chart Component's `model` prop (port of
// data_centrifuge/component.coffee:43-59).
// Advisory vectors are ordinary vectors everywhere except here: a parent that exists only because
// an Advisory response hit it would put an axis on the chart for a tier the chart does not plot.
// Its score-row position is dropped in the same pass — the rows are positional against `vectors`,
// so hiding one without the other silently misaligns every weight after it.
const hideAdvisoryOnlyAxes = data => {
  const hidden = new Set(data.advisoryOnlyVectorIds || [])
  const kept = (data.vectors || []).map((vector, index) => ({ vector, index })).filter(k => !hidden.has(k.vector.id))
  const project = rows => (rows || []).map(row => [row[0], ...kept.map(k => row[k.index + 1])])

  return { vectors: kept.map(k => k.vector), project }
}

export const buildComponentModel = data => {
  const { vectors, project } = hideAdvisoryOnlyAxes(data)

  return {
    vectors,
    disabledVectors: data.disabledVectors,
    autoEnabledLikeVectors: data.autoEnabledLikeVectors || [],
    scores: project(data.scores),
    scoresFiltered: project(data.scoresFiltered),
    answerScores: data.answerScores,
    suspicionScores: data.suspicionScores,
    excludedScores: data.excludedScores,
    filter: data.filter || DEFAULT_FILTER,
    suspicionLevel: data.suspicionLevel || DEFAULT_SUSPICION_LEVEL,
    quarantineUIDs: data.quarantineUIDs || [],
    quarantineUIDsFiltered: data.quarantineUIDsFiltered || [],
    dcLastRunTs: Object.hasOwn(data, 'dcLastRunTs') ? data.dcLastRunTs : null,
    dcAnalyzedCount: Object.hasOwn(data, 'dcAnalyzedCount') ? data.dcAnalyzedCount : 0,
    notAnalyzedCount: Object.hasOwn(data, 'notAnalyzedCount') ? data.notAnalyzedCount : 0,
    dcQuarantineEnabled: Object.hasOwn(data, 'dcQuarantineEnabled') ? data.dcQuarantineEnabled : false,
    advisoryFiltered: data.advisoryFiltered || []
  }
}

// Collect only the componentProps.data fields the Responses view (and its Response
// cards) actually read — keeps the prop surface minimal instead of handing over the
// whole data blob. The static icon/title/colour maps live in Responses.jsx itself.
export const buildResponsesModel = data => ({
  responses: data.responses,
  vectors: data.vectors,
  disabledVectors: data.disabledVectors,
  scores: data.scores,
  suspicionScores: data.suspicionScores,
  filter: data.filter || DEFAULT_FILTER,
  suspicionLevel: data.suspicionLevel || DEFAULT_SUSPICION_LEVEL,
  advisoryFiltered: data.advisoryFiltered || []
})

const DataCentrifuge = ({ controller, tier }) => {
  const [, forceRender] = useReducer(t => t + 1, 0)

  // Subscribe once; every controller.notify() triggers a re-render.
  useEffect(() => controller.subscribe(forceRender), [controller])

  // componentProps is mutated in place across renders (the _.isEqual diffing in the
  // builder keeps nested refs stable), so hold it in a ref, not state.
  const componentPropsRef = useRef(null)
  componentPropsRef.current = buildComponentProps(componentPropsRef.current, controller.snapshot())

  const { data, job } = componentPropsRef.current

  const componentModel = useMemo(
    () => buildComponentModel(data),
    [
      data.responses,
      data.vectors,
      data.disabledVectors,
      data.scores,
      data.suspicionScores,
      data.filter,
      data.suspicionLevel,
      data.quarantineUIDs,
      data.advisoryFiltered,
      data.advisoryOnlyVectorIds
    ]
  )
  const responsesModel = useMemo(
    () => buildResponsesModel(data),
    [
      data.responses,
      data.vectors,
      data.disabledVectors,
      data.scores,
      data.suspicionScores,
      data.filter,
      data.suspicionLevel,
      data.advisoryFiltered
    ]
  )

  // Both evidence surfaces -- this list and the chart tooltip's link -- gate on one resolved
  // answer, so neither can offer a review the other refuses.
  const reviewEvidence = controller.resolveReviewEvidence()

  const onFetch = useCallback(() => controller.delayFetchJob(), [controller])
  const onChange = useCallback((key, value) => controller.handleCallback({ [key]: value }), [controller])
  const onCallback = useCallback(params => controller.handleCallback(params), [controller])
  const onFilter = useCallback((pattern, filter) => controller.onFilter(pattern, filter), [controller])

  if (!controller.viewActive || !controller.componentData) return null

  return (
    <>
      <Component
        tier={tier}
        reviewEvidence={reviewEvidence}
        job={job}
        model={componentModel}
        features={controller.getFeatures()}
        onFetch={onFetch}
        onChange={onChange}
        onFilter={onFilter}
        onCallback={onCallback}
      />
      {/* Response-level evidence review needs the tier and the producer's own grant. It reaches
          below the administrative tier and the analysed count does not, so a settled run is read
          off the last-run timestamp — the count left every T1 clean run silent instead of stated. */}
      {reviewEvidence ? (
        <Responses
          tier={tier}
          model={responsesModel}
          settled={['idle', 'disabled'].includes(job.status) && Boolean(data.dcLastRunTs)}
          onRestore={controller.onRestore}
          onReject={controller.onReject}
          onScrollToQuestion={(id, batch) => controller.scrollToQuestion?.(id, batch)}
        />
      ) : null}
    </>
  )
}

DataCentrifuge.displayName = 'DataCentrifuge'

DataCentrifuge.propTypes = {
  controller: PropTypes.object.isRequired,
  tier: PropTypes.string
}

export default DataCentrifuge
